import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

import ActionQueue from "../components/ActionQueue";
import CommandCenter from "../components/CommandCenter";
import DashboardHeader from "../components/DashboardHeader";
import MorningBrief from "../components/MorningBrief";

const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/dashboard`;

export default function DashboardPage({ user, onLogout }) {
  const [dashboard, setDashboard] = useState(null);
  const [filter, setFilter] = useState("All");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queueRef = useRef(null);

  const remainingCount = useMemo(() => {
    if (!dashboard) {
      return 0;
    }
    return dashboard.actions.filter((action) => !action.is_complete).length;
  }, [dashboard]);

  const loadDashboard = async () => {
    setIsRefreshing(true);
    try {
      const response = await axios.get(apiUrl, { withCredentials: true });
      setDashboard(response.data);
    } catch (error) {
      toast.error("Cardfolio couldn’t refresh your workday.");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const handleActionCompletion = async (action) => {
    const updatedAction = { ...action, is_complete: !action.is_complete };
    setDashboard((current) => ({
      ...current,
      completed_count: current.completed_count + (updatedAction.is_complete ? 1 : -1),
      actions: current.actions.map((item) => (item.id === action.id ? updatedAction : item)),
    }));
    try {
      await axios.patch(
        `${apiUrl}/actions/${action.id}`,
        { is_complete: updatedAction.is_complete },
        { withCredentials: true },
      );
      toast.success(updatedAction.is_complete ? "Priority completed." : "Priority moved back to today.");
    } catch (error) {
      toast.error("That change could not be saved. Try again.");
      loadDashboard();
    }
  };

  const focusQueue = () => {
    queueRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!dashboard) {
    return (
      <main className="loading-screen" data-testid="dashboard-loading-state">
        <span className="loading-mark">C</span>
        <p>Preparing your workday</p>
      </main>
    );
  }

  return (
    <div className="dashboard-shell" data-testid="cardfolio-dashboard">
      <DashboardHeader user={user} onLogout={onLogout} activePage="Today" />
      <main className="dashboard-main">
        <div className="page-topline">
          <p data-testid="priority-count-label">{remainingCount} priorities remain for today</p>
          <button
            className="refresh-button"
            onClick={loadDashboard}
            type="button"
            data-testid="refresh-dashboard-button"
          >
            <RefreshCw className={isRefreshing ? "is-spinning" : ""} size={15} strokeWidth={1.8} />
            Refresh signals
          </button>
        </div>
        <MorningBrief data={dashboard} onFocusQueue={focusQueue} />
        <div className="dashboard-grid">
          <div ref={queueRef} className="queue-column">
            <ActionQueue
              actions={dashboard.actions}
              filter={filter}
              onFilter={setFilter}
              onComplete={handleActionCompletion}
            />
          </div>
          <CommandCenter data={dashboard} />
        </div>
      </main>
    </div>
  );
}